import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { AuditZaznam } from "@/api/hooks/useAudit";

/** Slovenské názvy tabuliek — v UI nikto nemá čítať názvy z DB. */
const TABULKY_SK: Record<string, string> = {
  zakazka: "Zákazka",
  varianta: "Výpočet",
  material_slot: "Materiál zákazky",
  zostava: "Zostava",
  dielec: "Dielec",
  material: "Materiál v cenníku",
  material_cena_override: "Cena materiálu v zákazke",
  porez_vysledok: "Porez",
  operation: "Operácia",
  klient: "Klient",
  sablona: "Šablóna",
  split_rule: "Pravidlo delenia",
  board_thickness: "Hrúbka dosky",
  nazov_dielca_preset: "Názov dielca",
  global_nastavenia: "Nastavenia",
  app_user: "Používateľ",
  dodavatel_uprava_cien: "Úprava cien dodávateľa",
  kategoria_marze: "Marže kategórií",
  preferovany_dodavatel: "Preferovaný dodávateľ",
  struktura_povrchu: "Štruktúra povrchu",
  prihlasenie: "Prihlásenie",
};

const AKCIE_SK: Record<string, string> = {
  INSERT: "Vytvorené",
  UPDATE: "Zmenené",
  DELETE: "Zmazané",
  // Prihlásenia (KOLO 68) — idú do toho istého audit logu, takže v tabe
  // Zmeny padnú do skupiny „Cenník, nastavenia a účty".
  LOGIN: "Prihlásenie",
  LOGIN_FAILED: "Neúspešné prihlásenie",
  LOGOUT: "Odhlásenie",
};

/** Kľúč skupiny pre záznamy, ktoré k žiadnej zákazke nepatria. */
const BEZ_ZAKAZKY = "__bez_zakazky__";
/** Kľúč podskupiny pre záznamy zákazky mimo zostáv. */
const BEZ_ZOSTAVY = "__bez_zostavy__";

interface Podskupina {
  kluc: string;
  zostavaKod: string | null;
  zaznamy: AuditZaznam[];
  najnovsie: number;
}

interface Skupina {
  kluc: string;
  zakazkaId: string | null;
  zakazkaCislo: string | null;
  zaznamov: number;
  najnovsie: number;
  podskupiny: Podskupina[];
}

/**
 * Zoskupí zmeny na **zákazka → zostava**.
 *
 * Plochý časový zoznam sa pri práci na viacerých zákazkách nedal čítať —
 * riadky z rôznych zákaziek a zostáv sa striedali. Zoskupenie je čisto
 * zobrazovacie: dáta prídu časovo zoradené a poradie skupín aj riadkov
 * v nich zostáva podľa času (najnovšie prvé), takže sa časové čítanie
 * nestráca, len dostane hlavičky.
 */
export function zoskup(zaznamy: AuditZaznam[]): Skupina[] {
  const mapa = new Map<string, Skupina>();

  for (const z of zaznamy) {
    const kluc = z.zakazka_id ?? BEZ_ZAKAZKY;
    const cas = new Date(z.created_at).getTime();
    let skupina = mapa.get(kluc);
    if (!skupina) {
      skupina = {
        kluc,
        zakazkaId: z.zakazka_id,
        zakazkaCislo: z.zakazka_cislo,
        zaznamov: 0,
        najnovsie: cas,
        podskupiny: [],
      };
      mapa.set(kluc, skupina);
    }
    skupina.zaznamov += 1;
    skupina.najnovsie = Math.max(skupina.najnovsie, cas);

    const podKluc = z.zostava_id ?? BEZ_ZOSTAVY;
    let pod = skupina.podskupiny.find((p) => p.kluc === podKluc);
    if (!pod) {
      pod = {
        kluc: podKluc,
        zostavaKod: z.zostava_kod,
        zaznamy: [],
        najnovsie: cas,
      };
      skupina.podskupiny.push(pod);
    }
    pod.zaznamy.push(z);
    pod.najnovsie = Math.max(pod.najnovsie, cas);
  }

  const skupiny = [...mapa.values()];
  for (const s of skupiny) {
    s.podskupiny.sort((a, b) => {
      // „Zákazka a výpočty" patrí na začiatok — je to kontext zostáv
      if (a.kluc === BEZ_ZOSTAVY) return -1;
      if (b.kluc === BEZ_ZOSTAVY) return 1;
      return b.najnovsie - a.najnovsie;
    });
  }
  skupiny.sort((a, b) => {
    // Cenník a nastavenia až za zákazkami — nie sú to zmeny na zákazke
    if (a.kluc === BEZ_ZAKAZKY) return 1;
    if (b.kluc === BEZ_ZAKAZKY) return -1;
    return b.najnovsie - a.najnovsie;
  });
  return skupiny;
}

function pocetSK(n: number): string {
  if (n === 1) return "1 zmena";
  if (n < 5) return `${n} zmeny`;
  return `${n} zmien`;
}

export interface AuditSkupinyProps {
  zaznamy: AuditZaznam[];
  /** Aktívny filter na zákazku — pri ňom je odkaz „len táto" zbytočný. */
  filtrovanaZakazka?: string;
  onFiltrujZakazku: (zakazkaId: string) => void;
}

export function AuditSkupiny({
  zaznamy,
  filtrovanaZakazka,
  onFiltrujZakazku,
}: AuditSkupinyProps): JSX.Element {
  const skupiny = useMemo(() => zoskup(zaznamy), [zaznamy]);
  // Zbalené držím ako výnimky — default je rozbalené, lebo na stránku
  // človek prichádza čítať zmeny, nie klikať. Stav je `useState`, nie
  // `localStorage`: zoznam zákaziek sa mení s filtrom aj stranou, takže
  // uložený stav by sa vzťahoval na skupiny, ktoré už nie sú na obrazovke.
  const [zbalene, setZbalene] = useState<Set<string>>(new Set());

  function prepni(kluc: string): void {
    setZbalene((prev) => {
      const next = new Set(prev);
      if (next.has(kluc)) next.delete(kluc);
      else next.add(kluc);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      {skupiny.map((s) => {
        const otvorena = !zbalene.has(s.kluc);
        const jeCennik = s.kluc === BEZ_ZAKAZKY;
        return (
          <section
            key={s.kluc}
            className="overflow-hidden rounded-lg border bg-card"
            data-testid={`audit-skupina-${s.zakazkaId ?? "bez-zakazky"}`}
          >
            <div className="flex items-center gap-2 border-b bg-background-alt/60 px-3 py-2">
              <button
                type="button"
                onClick={() => prepni(s.kluc)}
                aria-expanded={otvorena}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                title={otvorena ? "Zbaliť skupinu" : "Rozbaliť skupinu"}
              >
                {otvorena ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                {jeCennik ? (
                  <span className="text-sm font-semibold">
                    Cenník, nastavenia a účty
                  </span>
                ) : (
                  <span className="min-w-0 truncate text-sm font-semibold">
                    <span className="font-mono">
                      {s.zakazkaCislo ?? "—"}
                    </span>
                  </span>
                )}
                <span className="shrink-0 text-xs text-muted-foreground">
                  {pocetSK(s.zaznamov)}
                </span>
              </button>

              {!jeCennik && s.zakazkaId && s.zakazkaId !== filtrovanaZakazka ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0 text-xs"
                  onClick={() => onFiltrujZakazku(s.zakazkaId!)}
                  title="Zobraziť len zmeny tejto zákazky — skupina je inak len výsek aktuálnej strany"
                >
                  <Filter className="h-3.5 w-3.5" />
                  len táto zákazka
                </Button>
              ) : null}
            </div>

            {otvorena
              ? s.podskupiny.map((p) => (
                  <div key={p.kluc}>
                    <div
                      className={cn(
                        "flex items-center gap-2 border-b bg-background-alt/25 px-3 py-1",
                        "text-[11px] uppercase tracking-wide text-muted-foreground",
                      )}
                    >
                      <span className="font-semibold">
                        {p.kluc === BEZ_ZOSTAVY
                          ? jeCennik
                            ? "Cenník a nastavenia"
                            : "Zákazka a výpočty"
                          : `Zostava ${p.zostavaKod ?? "—"}`}
                      </span>
                      <span>· {pocetSK(p.zaznamy.length)}</span>
                    </div>
                    <Table>
                      <TableHeader className="sr-only">
                        <TableRow>
                          <TableHead className="w-8" />
                          <TableHead>Kedy</TableHead>
                          <TableHead>Kto</TableHead>
                          <TableHead>Druh</TableHead>
                          <TableHead>Čoho</TableHead>
                          <TableHead>Čo sa zmenilo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {p.zaznamy.map((z) => (
                          <ZaznamRow key={z.id} zaznam={z} />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))
              : null}
          </section>
        );
      })}
    </div>
  );
}

/** Jeden riadok histórie — rozbalí sa na rozdiel po poliach. */
export function ZaznamRow({ zaznam }: { zaznam: AuditZaznam }): JSX.Element {
  const [otvorene, setOtvorene] = useState(false);
  const maDetail = zaznam.zmeny.length > 0;

  return (
    <>
      <TableRow
        className={maDetail ? "cursor-pointer" : undefined}
        onClick={maDetail ? () => setOtvorene((o) => !o) : undefined}
        data-testid={`audit-row-${zaznam.id}`}
      >
        <TableCell className="w-8 p-1 text-center">
          {maDetail ? (
            otvorene ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </TableCell>
        <TableCell className="w-[150px] whitespace-nowrap text-xs text-muted-foreground">
          {new Date(zaznam.created_at).toLocaleString("sk-SK")}
        </TableCell>
        <TableCell className="w-[160px] text-sm">
          {zaznam.autor ?? "—"}
        </TableCell>
        <TableCell className="w-[110px]">
          <AkciaBadge action={zaznam.action} />
        </TableCell>
        <TableCell className="w-[180px] text-sm">
          {/* Zákazka a zostava sú v hlavičke skupiny — v riadku by to bol šum */}
          {TABULKY_SK[zaznam.tabulka] ?? zaznam.tabulka}
          {zaznam.popis ? (
            <span className="ml-1 font-mono text-xs text-muted-foreground">
              {zaznam.popis}
            </span>
          ) : null}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {maDetail
            ? zaznam.zmeny
                .map((z) => z.pole)
                .slice(0, 4)
                .join(", ") +
              (zaznam.zmeny.length > 4
                ? ` +${zaznam.zmeny.length - 4} ďalších`
                : "")
            : "—"}
        </TableCell>
      </TableRow>
      {otvorene ? (
        <TableRow>
          <TableCell colSpan={6} className="bg-background-alt/40 p-0">
            <Table>
              <TableBody>
                {zaznam.zmeny.map((z) => (
                  <TableRow key={z.pole}>
                    <TableCell className="w-[220px] font-mono text-xs">
                      {z.pole}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground line-through">
                      {formatHodnota(z.pred)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatHodnota(z.po)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function AkciaBadge({ action }: { action: string }): JSX.Element {
  // Neúspešné prihlásenie je červené ako mazanie — je to to, čo v zozname
  // hľadáš, keď kontroluješ, či sa niekto nepokúšal dostať dnu.
  const styl =
    action === "INSERT" || action === "LOGIN"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
      : action === "DELETE" || action === "LOGIN_FAILED"
        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
        : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${styl}`}>
      {AKCIE_SK[action] ?? action}
    </span>
  );
}

/** Hodnota z JSON snapshotu na čitateľný text. */
function formatHodnota(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "áno" : "nie";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 120 ? `${s.slice(0, 120)}…` : s;
}

export { TABULKY_SK };
